import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button, ControlledTabs } from '@/components/ui';
import { useAuth } from '@/store/auth-context';
import { useMeSpaces } from '@/hooks/useMeSpaces';
import { meetsRole } from '@/lib/space-roles';
import {
  useMyCredentials,
  useOrgCredentials,
  useSpaceCredentials,
} from '@/hooks/useIdentityCredentials';
import ProviderCard from './ProviderCard';
import AvailableProvidersList from './AvailableProvidersList';
import { scopeFromTabId, type Scope } from './scope';
import { providerRowKey } from './identity-lib';
import type {
  Credential,
  CredentialAuthType,
  CredentialListResponse,
  ProviderSummary,
} from '@/types/identity';

/**
 * Scope-tabbed credentials page. The active tab lives in `?scope=` so
 * deep-links survive reloads. The Personal tab (user-scope credentials)
 * comes first, then shared spaces alphabetically, then an Org tab — only
 * when the caller is an org admin.
 *
 * Non-admins still see an `Also available at org level` hint on space
 * cards when an org credential exists for that provider — fully read-only,
 * no details, no actions.
 */
export default function CredentialsPage() {
  const { user } = useAuth();
  const isOrgAdmin = user?.role === 'admin';
  const { data: spacesData, isLoading: spacesLoading } = useMeSpaces();
  const spaces = spacesData?.items ?? [];

  const [searchParams, setSearchParams] = useSearchParams();

  // Shared-space tabs only — Personal is now its own user-scope tab
  // (rendered unconditionally below), independent of any `kind:'personal'`
  // space the user may still have provisioned.
  const sharedSpaces = useMemo(
    () => spaces.filter((s) => s.kind !== 'personal').sort((a, b) => a.name.localeCompare(b.name)),
    [spaces],
  );

  const tabs = useMemo(() => {
    const personalTab = [{ id: 'user', label: 'Personal' }];
    const spaceTabs = sharedSpaces.map((s) => ({
      id: `space:${s.spaceId}`,
      label: s.name,
    }));
    const orgTab = isOrgAdmin ? [{ id: 'org', label: 'Org' }] : [];
    return [...personalTab, ...spaceTabs, ...orgTab];
  }, [sharedSpaces, isOrgAdmin]);

  // Personal is always present and is the natural default. Honour the
  // `?scope=` query param when it points at a tab we actually render.
  const fallbackTabId = 'user';
  const requestedTab = searchParams.get('scope') ?? fallbackTabId;
  const activeTab = tabs.some((t) => t.id === requestedTab)
    ? requestedTab
    : fallbackTabId;

  const scope = scopeFromTabId(activeTab, sharedSpaces);

  const setActiveTab = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('scope', id);
    setSearchParams(next, { replace: true });
  };

  // The `Also available at org level` hint depends on knowing which
  // providers the org has connected. `useOrgCredentials` only resolves
  // for org admins — non-admins don't have a redacted summary endpoint
  // yet, so they currently see no hint. Pending identity follow-up to
  // expose a `/api/v1/org/credentials/summary` (or equivalent) that
  // returns provider IDs without metadata for any authenticated user.
  const orgQuery = useOrgCredentials();
  const orgProviderIds = new Set(
    (orgQuery.data?.credentials ?? []).map((c) => c.provider),
  );

  return (
    <div className="max-w-4xl">
      <h1 className="text-2xl font-bold text-foreground mb-1">Credentials</h1>
      <p className="text-sm text-muted-foreground mb-6">
        Connect third-party services your agents can call. Switch tabs to
        manage credentials for yourself, a shared space, or your organization.
      </p>

      {spacesLoading ? (
        <LoadingPanel />
      ) : (
        <ControlledTabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab}>
          {scope && (
            <ScopePanel scope={scope} orgProviderIds={orgProviderIds} />
          )}
        </ControlledTabs>
      )}
    </div>
  );
}

interface ScopePanelProps {
  scope: Scope;
  orgProviderIds: Set<string>;
}

function ScopePanel({ scope, orgProviderIds }: ScopePanelProps) {
  const { user } = useAuth();
  const { data: spacesData } = useMeSpaces();
  const isOrgAdmin = user?.role === 'admin';

  // Pick the right hook per scope. Unused queries stay disabled so there's
  // no wasted network.
  const userQuery = useMyCredentials();
  const spaceQuery = useSpaceCredentials(
    scope.kind === 'space' ? scope.spaceId : undefined,
  );
  const orgQuery = useOrgCredentials();

  const query =
    scope.kind === 'user'
      ? userQuery
      : scope.kind === 'space'
        ? spaceQuery
        : orgQuery;

  const canManage =
    scope.kind === 'user'
      ? true
      : scope.kind === 'org'
        ? isOrgAdmin
        : (() => {
            const role = (spacesData?.items ?? []).find(
              (s) => s.spaceId === scope.spaceId,
            )?.role;
            return isOrgAdmin || meetsRole(role, 'admin');
          })();

  if (query.isLoading || query.isPending) return <LoadingPanel />;
  if (query.error) return <ErrorPanel error={query.error} onRetry={() => query.refetch()} />;

  const data = query.data as CredentialListResponse | undefined;
  if (!data) return <LoadingPanel />;

  return (
    <ScopeContent
      data={data}
      scope={scope}
      canManage={canManage}
      orgProviderIds={
        // Don't suggest "also at org level" on the Org tab itself.
        scope.kind === 'org' ? new Set() : orgProviderIds
      }
    />
  );
}

interface ScopeContentProps {
  data: CredentialListResponse;
  scope: Scope;
  canManage: boolean;
  orgProviderIds: Set<string>;
}

function ScopeContent({ data, scope, canManage, orgProviderIds }: ScopeContentProps) {
  const grouped = groupCredentialsByProvider(data.credentials, data.providers);
  const connected = grouped.filter((g) => g.credentials.length > 0);
  const connectedKeys = new Set(connected.map((g) => g.key));
  // A provider id that occurs in more than one row (e.g. Datadog via both
  // oauth_manual and api_key) gets a method subtitle so the cards/rows are
  // distinguishable. Single-method providers stay visually unchanged.
  const duplicateProviderIds = findDuplicateProviderIds(grouped);
  const summary = summarize(data.credentials);

  return (
    <div>
      <p className="text-xs text-muted-foreground mb-4">
        {summary}
      </p>

      {connected.length === 0 ? (
        <EmptyScopeState scope={scope} canManage={canManage} />
      ) : (
        <div className="space-y-4">
          {connected.map((group) => (
            <ProviderCard
              key={group.key}
              provider={group.provider}
              authType={group.authType}
              credentials={group.credentials}
              canManage={canManage}
              scope={scope}
              showMethod={duplicateProviderIds.has(group.provider.id)}
              alsoAtOrg={
                scope.kind !== 'org' && orgProviderIds.has(group.provider.id)
              }
            />
          ))}
        </div>
      )}

      <AvailableProvidersList
        providers={data.providers}
        scope={scope}
        canManage={canManage}
        connectedKeys={connectedKeys}
      />
    </div>
  );
}

interface ProviderGroup {
  /** Composite `${id}::${authType}::${providerInstanceId}` row identity. */
  key: string;
  provider: ProviderSummary;
  /** The row's single auth method — `provider.supportedAuthTypes[0]`. */
  authType: CredentialAuthType;
  credentials: Credential[];
}

/**
 * Group credentials into one card per `(provider, authType, providerInstanceId)`
 * row. The catalog now ships a separate row per auth method a provider exposes,
 * so a provider can legitimately appear more than once — keying on `provider.id`
 * alone (as before) would silently collapse those rows into one.
 */
export function groupCredentialsByProvider(
  credentials: Credential[],
  providers: ProviderSummary[],
): ProviderGroup[] {
  const byKey = new Map<string, ProviderGroup>();
  for (const provider of providers) {
    // Drop entries that arrive missing required keys — `ProviderSummary`
    // is typed as fully populated, but a backend regression could ship
    // partial records and we'd rather skip them than crash the page.
    if (!provider?.id || !Array.isArray(provider.supportedAuthTypes)) continue;
    // One card per auth method the provider advertises. A platform provider
    // that supports several (Atlassian = ['oauth2', 'api_key']) ships as a
    // single catalog entry, so reading only `[0]` would hide every method's
    // card but the first. MCP-derived summaries carry a single-element array,
    // so this stays one card for them.
    for (const authType of provider.supportedAuthTypes) {
      if (!authType) continue;
      const key = providerRowKey(provider.id, authType, provider.providerInstanceId);
      byKey.set(key, { key, provider, authType, credentials: [] });
    }
  }
  for (const cred of credentials) {
    // Skip credentials missing their `provider` discriminator — they'd
    // become an unkeyable group and React would warn about duplicate keys.
    if (!cred?.provider) continue;
    const key = providerRowKey(cred.provider, cred.authType, cred.providerInstanceId);
    const group = byKey.get(key);
    if (group) {
      group.credentials.push(cred);
    } else {
      // Credential references a row not in the catalog response — surface it
      // as a minimal entry so the user can still revoke it. Instance-bound
      // credentials of a platform provider (e.g. `aws-role` rows keyed
      // `<account>#<role>`) land here too; borrow the catalog entry's
      // display name so the card isn't titled with the raw provider id.
      const catalogEntry = providers.find((p) => p?.id === cred.provider);
      byKey.set(key, {
        key,
        provider: {
          id: cred.provider,
          displayName: catalogEntry?.displayName ?? cred.provider,
          supportedAuthTypes: [cred.authType],
          consumableAs: ['platform'],
          oauthAppMode: null,
          connectionCount: 1,
          providerInstanceId: cred.providerInstanceId,
          status:
            cred.status === 'connected'
              ? 'connected'
              : cred.status === 'error'
                ? 'error'
                : 'needs_reauth',
        },
        authType: cred.authType,
        credentials: [cred],
      });
    }
  }
  // Sort connected groups first (alphabetical within), then disconnected.
  // Tie-break by auth method so a provider's duplicate rows have a stable
  // order. Fall back to the provider id when displayName is missing — keeps
  // the page rendering even if a summary arrives half-populated.
  return Array.from(byKey.values()).sort((a, b) => {
    const aConn = a.credentials.length > 0 ? 0 : 1;
    const bConn = b.credentials.length > 0 ? 0 : 1;
    if (aConn !== bConn) return aConn - bConn;
    const aName = a.provider.displayName ?? a.provider.id ?? '';
    const bName = b.provider.displayName ?? b.provider.id ?? '';
    const byName = aName.localeCompare(bName);
    if (byName !== 0) return byName;
    return a.authType.localeCompare(b.authType);
  });
}

/** Provider ids that occur in more than one group — used to show a method subtitle. */
function findDuplicateProviderIds(groups: ProviderGroup[]): Set<string> {
  const counts = new Map<string, number>();
  for (const g of groups) {
    counts.set(g.provider.id, (counts.get(g.provider.id) ?? 0) + 1);
  }
  return new Set([...counts].filter(([, n]) => n > 1).map(([id]) => id));
}

function summarize(credentials: Credential[]): string {
  if (credentials.length === 0) return 'No credentials connected in this scope.';
  const counts = {
    connected: 0,
    needs_reauth: 0,
    error: 0,
    pending: 0,
    expired: 0,
  } as Record<string, number>;
  for (const c of credentials) counts[c.status] = (counts[c.status] ?? 0) + 1;
  const parts: string[] = [`${counts.connected ?? 0} connected`];
  if (counts.needs_reauth || counts.expired) {
    parts.push(`${(counts.needs_reauth ?? 0) + (counts.expired ?? 0)} need reauth`);
  }
  if (counts.error) parts.push(`${counts.error} error`);
  if (counts.pending) parts.push(`${counts.pending} pending`);
  return parts.join(' · ');
}

function LoadingPanel() {
  return (
    <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
      <Loader2 className="h-4 w-4 animate-spin" />
      <span className="text-sm">Loading credentials…</span>
    </div>
  );
}

function ErrorPanel({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <div className="border border-status-error-bg bg-status-error-bg/30 rounded-lg p-4 flex items-start gap-3">
      <AlertTriangle className="h-5 w-5 text-status-error-fg shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="font-medium text-foreground">Couldn't load credentials</p>
        <p className="text-sm text-muted-foreground mt-1">
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
        <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
          Retry
        </Button>
      </div>
    </div>
  );
}

function EmptyScopeState({ scope, canManage }: { scope: Scope; canManage: boolean }) {
  const label =
    scope.kind === 'org'
      ? 'the organization scope'
      : scope.kind === 'user'
        ? 'your personal credentials'
        : `the ${scope.spaceName} space`;
  return (
    <div className="border border-dashed border-border rounded-lg px-6 py-10 text-center bg-card">
      <h3 className="text-base font-medium text-foreground">
        No credentials in {label} yet
      </h3>
      <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
        {canManage
          ? 'Pick a provider below to connect — OAuth opens a new tab, API tokens are stored after validation.'
          : 'Ask a space admin to connect a provider on your behalf.'}
      </p>
    </div>
  );
}

